/**
 * Local draft state for SHIPPING modality (DeliveryPolicy — plan G9/G13).
 * Visibility: shippingLightEnabled + policy. Charge always 0 / omit — no methodId.
 * Prefills address from membership shippingAddressJson when available.
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isShippingModalityUxVisible,
  useEligibleShippingMethods,
  type EligibleShippingMethodsDTO,
} from '../shippingCheckoutApi.js';
import {
  membershipAddressToDraft,
  useMembershipShippingAddress,
  useSaveMembershipShippingAddress,
} from '../membershipShippingAddressApi.js';
import {
  EMPTY_SHIPPING_ADDRESS,
  toShippingCheckoutPayload,
  type ShippingAddressDraft,
  type ShippingCheckoutPayload,
} from '../shippingAddressTypes.js';
import {
  isShippingModalityReady,
  type FulfillmentModality,
} from '../components/FulfillmentModalityPanel.js';

export interface UseShippingModalityDraftResult {
  readonly eligible: EligibleShippingMethodsDTO | undefined;
  readonly eligibleLoading: boolean;
  readonly modalityVisible: boolean;
  readonly modality: FulfillmentModality;
  readonly setModality: (mode: FulfillmentModality) => void;
  readonly address: ShippingAddressDraft;
  readonly setAddress: (next: ShippingAddressDraft) => void;
  readonly saveAddressToMembership: boolean;
  readonly setSaveAddressToMembership: (next: boolean) => void;
  /** Always 0 when SHIPPING selected (out-of-band fee — never payable). */
  readonly quotedChargeMinor: 0 | null;
  readonly shippingReady: boolean;
  readonly buildShippingPayload: () => ShippingCheckoutPayload | null;
  /** Persist address to membership when Amazon checkbox is checked (soft-fail if API missing). */
  readonly persistMembershipAddressIfRequested: () => Promise<void>;
}

export function useShippingModalityDraft(input: {
  readonly salesPointId: number | null;
}): UseShippingModalityDraftResult {
  const { i18n } = useTranslation('customer');
  const uiLanguage = i18n.resolvedLanguage ?? i18n.language;
  const eligibleQuery = useEligibleShippingMethods(input.salesPointId);
  const membershipAddressQuery = useMembershipShippingAddress();
  const saveMembershipAddress = useSaveMembershipShippingAddress();
  const [modalityChoice, setModalityChoice] = useState<FulfillmentModality>('PICKUP');
  const [addressOverride, setAddressOverride] = useState<ShippingAddressDraft | null>(null);
  const [saveAddressToMembership, setSaveAddressToMembership] = useState(false);

  const eligible = eligibleQuery.data;
  const modalityVisible = isShippingModalityUxVisible(eligible);

  // Clamp SHIPPING → PICKUP when modality UX not visible (no effect / setState).
  const modality: FulfillmentModality =
    !modalityVisible && modalityChoice === 'SHIPPING' ? 'PICKUP' : modalityChoice;

  const setModality = useCallback(
    (mode: FulfillmentModality) => {
      if (mode === 'SHIPPING' && !modalityVisible) {
        setModalityChoice('PICKUP');
        return;
      }
      setModalityChoice(mode);
    },
    [modalityVisible]
  );

  const membershipPrefill = useMemo(
    () => membershipAddressToDraft(membershipAddressQuery.data ?? null),
    [membershipAddressQuery.data]
  );
  const address = addressOverride ?? membershipPrefill ?? EMPTY_SHIPPING_ADDRESS;
  const setAddress = useCallback((next: ShippingAddressDraft) => {
    setAddressOverride(next);
  }, []);

  // G15: same uiLanguage as FulfillmentModalityPanel display fail-closed chain.
  const shippingReady = isShippingModalityReady({
    modality,
    address,
    policy: eligible?.policy ?? null,
    uiLanguage,
  });

  const buildShippingPayload = (): ShippingCheckoutPayload | null => {
    if (modality !== 'SHIPPING') {
      return null;
    }
    return toShippingCheckoutPayload(address, {
      shippingChargeMinor: 0,
      saveAddressToMembership,
    });
  };

  const persistMembershipAddressIfRequested = async (): Promise<void> => {
    if (!saveAddressToMembership) {
      return;
    }
    try {
      await saveMembershipAddress.mutateAsync(address);
    } catch {
      // Soft-fail: checkout still carries saveAddressToMembership for server seal path.
    }
  };

  return {
    eligible,
    eligibleLoading: eligibleQuery.isLoading,
    modalityVisible,
    modality,
    setModality,
    address,
    setAddress,
    saveAddressToMembership,
    setSaveAddressToMembership,
    quotedChargeMinor: modality === 'SHIPPING' ? 0 : null,
    shippingReady,
    buildShippingPayload,
    persistMembershipAddressIfRequested,
  };
}
